/**
 * Sends outbox items to the API (docs/api.md "Surveys & sync"):
 *  - attachment.upload → POST /attachments (multipart, idempotent by id)
 *  - survey.*          → POST /sync/ops (idempotent by op id)
 *
 * Network problems, 5xx, 401 (token refresh) and account errors are retried
 * later — captured work is never thrown away because of a connectivity or
 * session problem; an account error reaches the app once, through the
 * ApiClient these requests use. Domain rejections (e.g. LOCK_LOST,
 * INVALID_STATE) stop that survey's queue and are shown to the user, with
 * their details (the validation issues of a refused submit).
 *
 * Two refusals are final and handled without blocking the survey (`drop`):
 *  - a file the server will never accept (type, size): the app removes it
 *    from the answers and tells the reporter (onAttachmentRefused);
 *  - whatever onReject decides to drop (a refused submit returns the survey
 *    to editing; a discard the server no longer needs).
 */
import type { OutboxItem, SendResult, Transport } from '@sss/sync';
import { ApiError, isAccountError, type ApiClient } from '../api/client';
import type { SyncOpResult } from '../api/types';
import type { AttachmentRecord, OfflineStorage } from '../offline/types';

export interface TransportHooks {
  onResult?(item: OutboxItem, data: SyncOpResult['data'] | undefined): Promise<void> | void;
  /** Return 'drop' when the rejection is fully handled and must not block the survey. */
  onReject?(item: OutboxItem, error: string, details?: unknown): Promise<'drop' | void> | 'drop' | void;
  /** An upload the server refused for good (type/size). The attachment is gone from the queue. */
  onAttachmentRefused?(item: OutboxItem, attachmentId: string, code: string): Promise<void> | void;
  onUpgradeRequired?(): void;
}

/** Upload refusals that no retry can fix. */
export const PERMANENT_UPLOAD_ERRORS = ['UNSUPPORTED_TYPE', 'TOO_LARGE', 'PAYLOAD_TOO_LARGE', 'FILE_REQUIRED'] as const;

export class HttpTransport implements Transport {
  constructor(
    private readonly api: ApiClient,
    private readonly storage: OfflineStorage,
    private readonly hooks: TransportHooks = {},
  ) {}

  async send(item: OutboxItem): Promise<SendResult> {
    try {
      if (item.op === 'attachment.upload') return await this.upload(item);
      const res = await this.api.post<{ results: SyncOpResult[] }>('/sync/ops', {
        ops: [{ id: item.id, op: item.op, entityId: item.entityId, payload: item.payload }],
      });
      const r = res?.results?.find((x) => x.id === item.id) ?? res?.results?.[0];
      if (!r) return { status: 'retry', error: 'EMPTY_RESULT' };
      if (r.status === 'ok') {
        await this.hooks.onResult?.(item, r.data);
        return { status: 'ok' };
      }
      const error = r.error ?? 'REJECTED';
      return await this.rejected(item, error, r.details);
    } catch (e) {
      return this.classify(item, e);
    }
  }

  private async rejected(item: OutboxItem, error: string, details?: unknown): Promise<SendResult> {
    const decision = await this.hooks.onReject?.(item, error, details);
    // Nothing on the device depends on a discard any more (the draft is gone).
    if (decision === 'drop' || item.op === 'survey.discard') return { status: 'drop', error };
    return { status: 'reject', error };
  }

  private async upload(item: OutboxItem): Promise<SendResult> {
    const { attachmentId } = item.payload as { attachmentId: string };
    const a = await this.storage.getAttachment(attachmentId);
    // Deleted on the device before it was uploaded: nothing to send.
    if (!a) return { status: 'ok' };
    if (!a.uploaded) {
      // Idempotent by id: a retry after a lost response gets 201 again. A 409
      // ATTACHMENT_ID_CONFLICT (different bytes under the same id) is a genuine
      // conflict and is rejected via classify(), stopping this survey's queue.
      try {
        await this.sendFile(a);
      } catch (e) {
        if (e instanceof ApiError && (PERMANENT_UPLOAD_ERRORS as readonly string[]).includes(e.code)) {
          await this.hooks.onAttachmentRefused?.(item, a.id, e.code);
          return { status: 'drop', error: e.code };
        }
        throw e;
      }
      // Deleted on the device while it was uploading (photo removed, survey discarded): it stays deleted.
      const still = await this.storage.getAttachment(a.id);
      if (still) await this.storage.putAttachment({ ...still, uploaded: true });
    }
    await this.hooks.onResult?.(item, undefined);
    return { status: 'ok' };
  }

  private async sendFile(a: AttachmentRecord): Promise<void> {
    await this.api.upload(
      '/attachments',
      {
        id: a.id,
        ...(a.eventId ? { eventId: a.eventId } : { surveyId: a.surveyId }),
        kind: a.kind,
        lat: a.lat != null ? String(a.lat) : undefined,
        lng: a.lng != null ? String(a.lng) : undefined,
        takenAt: a.takenAt ?? undefined,
      },
      new Blob([a.data], { type: a.contentType }),
      a.fileName,
    );
  }

  private async classify(item: OutboxItem, e: unknown): Promise<SendResult> {
    if (!(e instanceof ApiError)) return { status: 'retry', error: e instanceof Error ? e.message : String(e) };
    if (e.isRetryable || e.status === 401) return { status: 'retry', error: e.code };
    if (e.status === 426 || e.code === 'UPGRADE_REQUIRED') {
      this.hooks.onUpgradeRequired?.();
      return { status: 'retry', error: e.code };
    }
    // Already reported to the app by the ApiClient (onAuthFailure); just wait for the account to be sorted out.
    if (isAccountError(e.code)) return { status: 'retry', error: e.code };
    return this.rejected(item, e.code, e.details);
  }
}
